/**
 * Tiered bundle — moteur d'affichage des prix « marginal cost ».
 *
 * Principe : chaque prix affiché = ce que l'item AJOUTERAIT au panier
 * (computeTotal(state ∪ item) − computeTotal(state)), avec ré-application
 * complète des règles Shopify (TB + CD), cheapest-first allocation pour les
 * quotas, max par unité pour la résolution.
 *
 * Modes d'affichage :
 *
 *   • Single variant (data-tb-price-varies absent) :
 *     <span class="price__regular">prix</span>
 *     + strikethrough si compare_at > prix
 *     + badge mis à jour (data-tb-badge sur .badges[data-product-id]).
 *
 *   • Multi-variantes à prix différents (data-tb-price-varies="true") :
 *     <span class="price__regular price__regular--from">Dès <prix réduit></span>
 *     SANS strikethrough.
 *     Badge mis à jour avec « Jusqu'à -X% off ».
 *     Le <span class="price__discount-range"> qui contenait le « Jusqu'à »
 *     inline est supprimé (info redondante avec le badge).
 *
 *   • Items du bloc additionnel : prix figé à la coche (lockedQueue),
 *     recalculé en ordre si un item antérieur est décoché.
 *
 * Coexistence CD : la logique CD est intégrée à computeTotal via
 * window.__conditionalDiscount.rules. Si CD est chargé séparément, il modifie
 * aussi le DOM mais TB le surcharge (chargé après dans theme.liquid). TB
 * supprime les badges CD ([data-cd-badge]) pour éviter les doublons.
 *
 * Pas de MutationObserver global (qui boucle avec les écritures TB et fige
 * la page) ; on s'appuie sur les événements pubsub / DOM ciblés.
 */

(function () {
  "use strict";

  const tbConfig = window.__tieredBundle;
  if (!tbConfig) return;

  const tbRules = (Array.isArray(tbConfig.rules) ? tbConfig.rules : [])
    .map((r) => ({
      productIdSet: new Set(r.productIds || []),
      tiers: (r.tiers || []).slice().sort((a, b) => a.qty - b.qty),
    }))
    .filter((r) => r.productIdSet.size > 0 && r.tiers.length > 0);
  if (!tbRules.length) return;

  function getCdRules() {
    const cfg = window.__conditionalDiscount;
    if (!cfg || !Array.isArray(cfg.rules)) return [];
    return cfg.rules.map((r) => ({
      requiredProductIds: new Set(r.requiredProductIds || []),
      discountedProductIds: new Set(r.discountedProductIds || []),
      requiredQty: r.requiredQty || 1,
      discountedQty: r.discountedQty || 1,
      discountPct: r.discountPct || 0,
    }));
  }

  // ─── Formatage monétaire / markup ────────────────────────────────────────────

  function formatMoney(cents) {
    const fmt = theme?.settings?.currencyCodeEnabled
      ? theme?.settings?.moneyWithCurrencyFormat
      : theme?.settings?.moneyFormat;
    if (window.theme?.Currency?.formatMoney && fmt) {
      return theme.Currency.formatMoney(cents, fmt);
    }
    return (cents / 100).toFixed(2);
  }

  function fromTemplate(el) {
    return (
      el?.dataset?.tbFromTemplate ||
      window.theme?.strings?.tbFromTemplate ||
      window.theme?.strings?.tbFromMinTemplate ||
      "From __amount__"
    );
  }

  function upToTemplate(el) {
    return (
      el?.dataset?.tbUpToTemplate ||
      window.theme?.strings?.tbUpToTemplate ||
      window.theme?.strings?.upToOffTemplate ||
      "Up to __amount__ off"
    );
  }

  // Construit la valeur à injecter dans une template type « -__amount__% off »
  // de façon défensive : si la template porte déjà le « - » devant __amount__
  // (cas FR historique : « Jusqu'à -__amount__ »), on n'ajoute pas le nôtre.
  function pctValueForTemplate(tpl, pct) {
    const hasMinusInTpl = /-__amount__/.test(tpl);
    return (hasMinusInTpl ? "" : "-") + pct + "%";
  }

  // Badge inline « -X% » / « Sale » pour usage dans les blocs sans .badges
  // (bundle items notamment). Format identique au badge natif Liquid.
  function inlineBadgeHtml(price, comparePrice) {
    if (!tbConfig.saveAmountEnabled || !comparePrice || comparePrice <= price)
      return "";
    const cls =
      "badge badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full";
    let inner;
    if (price === 0 && tbConfig.freeBadgeEnabled) {
      inner = tbConfig.i18n.free;
    } else if (tbConfig.saveType === "label") {
      inner = tbConfig.i18n.sale;
    } else if (tbConfig.saveType === "dollar") {
      const tpl = tbConfig.i18n.saveHtml || "Save __amount__";
      inner = tpl.replace("__amount__", formatMoney(comparePrice - price));
    } else {
      const pct = Math.round(((comparePrice - price) * 100) / comparePrice);
      if (pct <= 0) return "";
      const tpl = tbConfig.i18n.saveHtml || "Save __amount__";
      inner = tpl.replace("__amount__", pctValueForTemplate(tpl, pct));
    }
    return `<span class="${cls}">${inner}</span>`;
  }

  // « Dès <prix> » — pas de strikethrough. includeUpTo + upToPct ajoutent un
  // span .price__discount-range inline (utilisé dans le bloc additionnel
  // et les cartes horizontales, masqué via CSS dans .product-card).
  function fromMarkup(price, el, includeUpTo, upToPct) {
    const tpl = fromTemplate(el);
    const fromText = tpl.replace("__amount__", formatMoney(Math.max(0, price)));
    let html = `<span class="price__regular price__regular--from whitespace-nowrap">${fromText}</span>`;
    if (includeUpTo && upToPct > 0) {
      const upTpl = upToTemplate(el);
      const upText = upTpl.replace(
        "__amount__",
        pctValueForTemplate(upTpl, upToPct),
      );
      html += `<span class="price__discount-range whitespace-nowrap">${upText}</span>`;
    }
    return html;
  }

  // Discount markup. includeBadge ajoute un badge inline (.badge--onsale)
  // — réservé aux contextes sans .badges container (bundle items).
  function discountedMarkup(price, cap, includeBadge) {
    let html =
      `<span class="sr-only">${tbConfig.i18n.salePriceLabel}</span>` +
      `<span class="price__regular whitespace-nowrap">${formatMoney(price)}</span>` +
      `<span class="sr-only">${tbConfig.i18n.regularPriceLabel}</span>` +
      `<span class="price__sale inline-flex items-center h-auto relative">${formatMoney(cap)}</span>`;
    if (includeBadge) html += inlineBadgeHtml(price, cap);
    return html;
  }

  function regularMarkup(price, compareAtPrice, includeBadge) {
    const priceFmt = formatMoney(price);
    if (compareAtPrice > price) {
      let html =
        `<span class="sr-only">${tbConfig.i18n.salePriceLabel}</span>` +
        `<span class="price__regular whitespace-nowrap">${priceFmt}</span>` +
        `<span class="sr-only">${tbConfig.i18n.regularPriceLabel}</span>` +
        `<span class="price__sale inline-flex items-center h-auto relative">${formatMoney(compareAtPrice)}</span>`;
      if (includeBadge) html += inlineBadgeHtml(price, compareAtPrice);
      return html;
    }
    return `<span class="price__regular whitespace-nowrap">${priceFmt}</span>`;
  }

  // Trouve la product-info associée à un élément, même quand l'élément est
  // HORS du <product-info> (cas du sticky, qui est sibling de product-info
  // mais dans la même <section>). Recherche d'abord en ancêtre direct, puis
  // dans la section/scope englobant via data-product-id.
  function getScopeForProductInfo(productInfo) {
    if (!productInfo) return document;
    return (
      productInfo.closest(".quick-view, .x-modal") ||
      productInfo.closest(".featured-product") ||
      productInfo.closest(".shopify-section, section") ||
      document
    );
  }

  function findProductInfoFor(el) {
    if (!el || !el.closest) return null;
    let pi = el.closest("product-info");
    if (pi) return pi;
    const pid =
      el.dataset?.productId ||
      el.getAttribute?.("data-product-id") ||
      el.closest?.("[data-product-id]")?.dataset?.productId;
    if (!pid) return null;
    const scope =
      el.closest(
        ".quick-view, .x-modal, .featured-product, .shopify-section, section",
      ) || document;
    return scope.querySelector(`product-info[data-product-id="${pid}"]`);
  }

  // custom_preselect-variant.js : l'utilisateur doit cliquer chaque option au
  // moins une fois avant que la sélection soit considérée « complète ».
  function isVariantSelectionPending(productInfo) {
    if (!productInfo) return false;
    const container = productInfo.closest("[data-validation-initialized]");
    if (!container) return false;
    return container.getAttribute("data-variant-selection-complete") !== "true";
  }

  function getVariantPickerOptionCount(variantPicker) {
    if (!variantPicker) return 0;
    return variantPicker.querySelectorAll("fieldset.product-form__input")
      .length;
  }

  // Variante entièrement sélectionnée (toutes les options du picker).
  function hasCompleteVariantSelection(productInfo) {
    const variantPicker = productInfo?.querySelector("variant-picker");
    if (!variantPicker) return true;
    const totalOptions = getVariantPickerOptionCount(variantPicker);
    if (totalOptions === 0) return true;

    const checkedInputs = variantPicker.querySelectorAll(
      'input[type="radio"]:checked',
    );
    if (checkedInputs.length >= totalOptions) return true;

    const selects = variantPicker.querySelectorAll("select");
    let filledSelects = 0;
    selects.forEach((sel) => {
      if (sel.value !== "" && sel.value != null) filledSelects++;
    });
    return checkedInputs.length + filledSelects >= totalOptions;
  }

  function shouldShowVariantPreview(productInfo) {
    if (!productInfo) return false;
    if (isVariantSelectionPending(productInfo)) return true;
    return !hasCompleteVariantSelection(productInfo);
  }

  function hasSamePriceVariantSale(variants) {
    if (!variants || variants.length < 2) return false;
    const prices = variants.filter((v) => v.price > 0).map((v) => v.price);
    if (prices.length === 0) return false;
    if (Math.min(...prices) !== Math.max(...prices)) return false;
    return variants.some(
      (v) => v.compare_at_price && v.price && v.compare_at_price > v.price,
    );
  }

  function getSamePriceSalePreviewData(variants) {
    if (!hasSamePriceVariantSale(variants)) return null;
    const pricedVariants = variants.filter((v) => v.price > 0);
    const minPrice = Math.min(...pricedVariants.map((v) => v.price));
    let maxCompareAt = 0;
    pricedVariants.forEach((v) => {
      if (
        v.price === minPrice &&
        v.compare_at_price > v.price &&
        v.compare_at_price > maxCompareAt
      ) {
        maxCompareAt = v.compare_at_price;
      }
    });
    if (maxCompareAt <= minPrice) return null;
    return { minPrice, maxCompareAt };
  }

  // Mode « Dès … » / aperçu :
  //  • Tant que la validation variante n'est pas terminée (custom_preselect)
  //    ou qu'il manque des options → mode aperçu (« Jusqu'à … » ou promo max).
  //  • Après sélection complète d'une variante pour CE produit → prix/badge
  //    spécifiques à la variante (comme sans tiered bundle).
  //  • Le test productId évite que les bundle items suivent l'état de la fiche.
  function isFromMode(el) {
    if (!el) return false;
    const pi = findProductInfoFor(el);
    const elPid = el.dataset?.productId;
    const piPid = pi?.dataset?.productId;
    const sameProduct = !elPid || !piPid || String(elPid) === String(piPid);

    if (pi && sameProduct && shouldShowVariantPreview(pi)) {
      const variants = getProductVariants(
        parseInt(pi.dataset.productId, 10),
        el,
      );
      if (hasSamePriceVariantSale(variants)) return false;
      if (el.dataset.tbPriceVaries === "true") return true;
      if (el.querySelector(".price__regular--from")) return true;
      if (el.dataset.priceMax && el.dataset.priceMin) {
        const max = parseInt(el.dataset.priceMax, 10);
        const min = parseInt(el.dataset.priceMin, 10);
        if (Number.isFinite(max) && Number.isFinite(min) && max > min)
          return true;
      }
      return false;
    }

    if (pi && sameProduct && hasCompleteVariantSelection(pi)) return false;
    if (el.dataset.tbVariantSelected === "true") return false;
    if (el.dataset.tbPriceVaries === "true") return true;
    if (el.querySelector(".price__regular--from")) return true;
    if (el.dataset.priceMax && el.dataset.priceMin) {
      const max = parseInt(el.dataset.priceMax, 10);
      const min = parseInt(el.dataset.priceMin, 10);
      if (Number.isFinite(max) && Number.isFinite(min) && max > min)
        return true;
    }
    return false;
  }

  // ─── Badges ──────────────────────────────────────────────────────────────────

  // Met à jour les .badges[data-product-id="..."] pour refléter la remise TB.
  // scopeRoot limite la mise à jour au modal / fiche concerné (évite la désync
  // fiche + quick-view pour le même product-id).
  function applyTbBadge(
    productId,
    displayed,
    basePrice,
    comparePrice,
    fromMode,
    upToPct,
    scopeRoot,
  ) {
    const root = scopeRoot && scopeRoot.querySelectorAll ? scopeRoot : document;
    root
      .querySelectorAll(`.badges[data-product-id="${productId}"]`)
      .forEach((badgesEl) => {
        badgesEl
          .querySelectorAll(
            "[data-tb-badge], [data-cd-badge], [data-variant-validation-badge]",
          )
          .forEach((b) => b.remove());

        const nativeBadges = badgesEl.querySelectorAll(
          ".badge--onsale:not([data-tb-badge]):not([data-cd-badge])",
        );
        nativeBadges.forEach((b) => {
          b.style.display = "";
        });

        const tbAffects = displayed < basePrice;
        const onCompareSale = comparePrice > basePrice;
        if (!tbAffects && !fromMode && !onCompareSale) return;
        if (!tbConfig.saveAmountEnabled) return;

        let inner = null;
        if (displayed === 0 && tbConfig.freeBadgeEnabled) {
          inner = tbConfig.i18n.free;
        } else if (tbConfig.saveType === "label") {
          inner = tbConfig.i18n.sale;
        } else {
          let pct;
          if (fromMode) {
            pct = upToPct;
          } else {
            const ref = comparePrice > basePrice ? comparePrice : basePrice;
            pct = ref > 0 ? Math.round(((ref - displayed) * 100) / ref) : 0;
            if (pct <= 0 && comparePrice > displayed) {
              pct = Math.round(
                ((comparePrice - displayed) * 100) / comparePrice,
              );
            }
          }
          if (pct <= 0) return;

          let tpl;
          if (fromMode) {
            tpl =
              badgesEl.dataset.badgeUpToTemplate ||
              window.theme?.strings?.upToOffTemplate ||
              "Up to __amount__ off";
          } else if (tbConfig.saveType === "dollar") {
            const ref = comparePrice > basePrice ? comparePrice : basePrice;
            const saved = formatMoney(ref - displayed);
            tpl =
              badgesEl.dataset.badgeSaveTemplate ||
              tbConfig.i18n.saveHtml ||
              "Save __amount__";
            inner = tpl.replace("__amount__", saved);
          } else {
            tpl =
              badgesEl.dataset.badgeSaveTemplate ||
              tbConfig.i18n.saveHtml ||
              "Save __amount__";
          }
          if (inner == null) {
            inner = tpl.replace("__amount__", pctValueForTemplate(tpl, pct));
          }
        }

        if (!inner) return;

        nativeBadges.forEach((b) => {
          b.style.display = "none";
        });

        const isVert = !!badgesEl.querySelector(".badge--vertical");
        const cls = `badge${isVert ? " badge--vertical" : ""} badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full`;
        const tmp = document.createElement("div");
        tmp.innerHTML = `<span class="${cls}">${inner}</span>`;
        const badge = tmp.firstElementChild;
        if (badge) {
          badge.dataset.tbBadge = "1";
          badgesEl.appendChild(badge);
        }
      });
  }

  // ─── Variantes (data-product-variant-data) ──────────────────────────────────

  // Map productId → array de variantes (price, compare_at_price).
  // Priorité : data-tb-variants sur l'élément (disponible sur les cartes où
  // il n'y a pas de product-info). Sinon, script[data-product-variant-data]
  // dans product-info (fiche/modal).
  function getProductVariants(productId, hintEl) {
    if (hintEl) {
      const raw = hintEl.dataset && hintEl.dataset.tbVariants;
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            return parsed.map((v) => ({
              price: v.p,
              compare_at_price: v.c,
            }));
          }
        } catch (_) {
          /* ignore */
        }
      }
    }
    const candidates = document.querySelectorAll(
      `product-info[data-product-id="${productId}"]`,
    );
    for (const pi of candidates) {
      const scriptTag = pi.querySelector("script[data-product-variant-data]");
      if (!scriptTag) continue;
      try {
        const data = JSON.parse(scriptTag.textContent);
        if (Array.isArray(data.productVariants)) return data.productVariants;
      } catch (_) {
        /* ignore */
      }
    }
    return null;
  }

  // Calcule le pourcentage MAX de remise observable parmi toutes les variantes
  // d'un produit (en réappliquant TB + CD pour chacune).
  // stateUnits : ÉTAT de base auquel on ajoute hypothétiquement chaque variante.
  //   • Cards / fiche / modal : panier seul.
  //   • Bundle items non cochés : panier + page (qty) + cochés (= baseStateUnits).
  // hintEl : élément .price (ou bundle-price) pour lire data-tb-variants en
  //   fallback du script[data-product-variant-data] des cartes.
  function computeMaxDiscountAcrossVariants(
    productId,
    stateUnits,
    cdRules,
    hintEl,
  ) {
    const variants = getProductVariants(productId, hintEl);
    if (!variants || variants.length === 0) return 0;
    let maxPct = 0;
    for (const v of variants) {
      const variantPrice = parseInt(v.price, 10);
      const compareAtPrice = parseInt(v.compare_at_price, 10) || 0;
      if (!Number.isFinite(variantPrice) || variantPrice <= 0) continue;
      const withV = [
        ...stateUnits,
        { productId, basePrice: variantPrice, comparePrice: compareAtPrice },
      ];
      // Coût marginal capé au prix de la variante : si l'ajout déplace une
      // remise sur un item plus cher, le pct effectif peut être nul (capé).
      const net = cappedMarginal(stateUnits, withV, variantPrice, cdRules);
      const ref = compareAtPrice > variantPrice ? compareAtPrice : variantPrice;
      if (ref <= 0) continue;
      const pct = ((ref - net) * 100) / ref;
      if (pct > maxPct) maxPct = pct;
    }
    return Math.round(maxPct);
  }

  // ─── Helpers tier ────────────────────────────────────────────────────────────

  function tierPctFor(qty, tiers) {
    let pct = 0;
    for (const t of tiers) {
      if (qty >= t.qty) pct = t.pct;
      else break;
    }
    return pct;
  }

  // ─── État panier ─────────────────────────────────────────────────────────────

  let cartData = tbConfig.cartData || null;

  function getCartUnits() {
    if (!cartData) return [];
    const out = [];
    for (const it of cartData.items) {
      for (let i = 0; i < it.quantity; i++) {
        out.push({
          productId: it.product_id,
          basePrice: it.price || 0,
          comparePrice: it.compare_at_price || 0,
        });
      }
    }
    return out;
  }

  // Construit la liste des contextes « page » : un par product-info présent
  // dans le DOM (fiche produit + chaque modal ouvert). Chaque contexte a son
  // propre scope, priceEl, qty selector — ce qui permet au sticky / ATC du
  // modal d'utiliser le marginal calculé avec la qty du modal (pas celle de
  // la fiche).
  function getPageContexts() {
    const contexts = [];
    document.querySelectorAll("product-info[data-product-id]").forEach((pi) => {
      const pid = parseInt(pi.dataset.productId, 10);
      if (!Number.isFinite(pid)) return;
      const scope = getScopeForProductInfo(pi);
      const root = scope === document ? document : scope;
      const priceEl = root.querySelector(
        `.price[data-product-id="${pid}"][data-base-price]:not([data-bundle-price])`,
      );
      if (!priceEl) return;
      const basePrice = parseInt(priceEl.dataset.basePrice, 10);
      const comparePrice =
        parseInt(priceEl.dataset.baseCompareAtPrice, 10) || 0;
      if (!Number.isFinite(basePrice) || basePrice <= 0) return;
      const qtyInput = root.querySelector(
        `quantity-input[data-product-id="${pid}"] input[type="number"]`,
      );
      let qty = 1;
      if (qtyInput) {
        const v = parseInt(qtyInput.value, 10);
        if (Number.isFinite(v) && v > 0) qty = v;
      }
      contexts.push({
        productId: pid,
        basePrice,
        comparePrice,
        qty,
        priceEl,
        scope: root,
        productInfo: pi,
      });
    });
    return contexts;
  }

  // Retourne le contexte qui contient l'élément, ou null.
  function findContextForElement(el, contexts) {
    for (const ctx of contexts) {
      if (ctx.scope === document) continue; // on préfère un modal d'abord
      if (ctx.scope.contains(el)) return ctx;
    }
    return contexts.find((c) => c.scope === document) || null;
  }

  // Backward-compat : premier contexte (= fiche). Utilisé par lockedQueue.
  function getPageInfo() {
    return getPageContexts()[0] || null;
  }

  // ─── File de coches ordonnée ─────────────────────────────────────────────────

  const lockedQueue = []; // { priceEl, productId, basePrice, comparePrice, lockedPrice }

  function pruneStaleQueueEntries() {
    for (let i = lockedQueue.length - 1; i >= 0; i--) {
      if (
        !lockedQueue[i].priceEl ||
        !document.body.contains(lockedQueue[i].priceEl)
      ) {
        lockedQueue.splice(i, 1);
      }
    }
  }

  // ProductBundleDetails décoche sans event change → retirer les entrées orphelines
  // pour ne pas double-compter dans baseStateUnits ni figer un prix périmé.
  function syncLockedQueueWithDom() {
    pruneStaleQueueEntries();
    for (let i = lockedQueue.length - 1; i >= 0; i--) {
      const priceEl = lockedQueue[i].priceEl;
      const bundleItem = priceEl?.closest?.("[data-bundle-item]");
      const checkbox = bundleItem?.querySelector('input[name="bundles"]');
      if (!checkbox?.checked) lockedQueue.splice(i, 1);
    }
  }

  function queueIndexOf(priceEl) {
    return lockedQueue.findIndex((it) => it.priceEl === priceEl);
  }

  // Recalcule lockedPrice d'un item à l'index idx = coût marginal d'ajout
  // séquentiel : panier + fiche + items cochés AVANT idx (ordre de coche).
  // Les items déjà cochés conservent leur prix figé quand on en coche un nouveau.
  function buildBasePageState(cartUnits, pageInfo) {
    const state = [...cartUnits];
    if (pageInfo) {
      for (let i = 0; i < pageInfo.qty; i++) {
        state.push({
          productId: pageInfo.productId,
          basePrice: pageInfo.basePrice,
          comparePrice: pageInfo.comparePrice,
        });
      }
    }
    return state;
  }

  function buildStateUpToQueueIndex(idx, cartUnits, pageInfo) {
    const state = buildBasePageState(cartUnits, pageInfo);
    for (let j = 0; j < idx; j++) {
      const li = lockedQueue[j];
      state.push({
        productId: li.productId,
        basePrice: li.basePrice,
        comparePrice: li.comparePrice,
      });
    }
    return state;
  }

  function computeLockedPriceAtIndex(idx, cartUnits, pageInfo, cdRules) {
    const it = lockedQueue[idx];
    if (!it) return;
    const stateBefore = buildStateUpToQueueIndex(idx, cartUnits, pageInfo);
    const stateAfter = [
      ...stateBefore,
      {
        productId: it.productId,
        basePrice: it.basePrice,
        comparePrice: it.comparePrice,
      },
    ];
    it.lockedPrice = cappedMarginal(
      stateBefore,
      stateAfter,
      it.basePrice,
      cdRules,
    );
  }

  // Recalcule à partir de fromIndex (décochage d'un item antérieur, changement
  // de variante, ou mise à jour panier).
  function recomputeLockedQueueSequential(
    fromIndex,
    cartUnits,
    pageInfo,
    cdRules,
  ) {
    pruneStaleQueueEntries();
    if (lockedQueue.length === 0) return;
    const cart = cartUnits || getCartUnits();
    const page = pageInfo !== undefined ? pageInfo : getPageInfo();
    const rules = cdRules || getCdRules();
    const start = Math.max(0, fromIndex);
    for (let i = start; i < lockedQueue.length; i++) {
      computeLockedPriceAtIndex(i, cart, page, rules);
    }
  }

  function enqueueLock(priceEl, productId, basePrice, comparePrice) {
    pruneStaleQueueEntries();
    if (queueIndexOf(priceEl) !== -1) return;
    lockedQueue.push({
      priceEl,
      productId,
      basePrice,
      comparePrice,
      lockedPrice: 0,
    });
    const idx = lockedQueue.length - 1;
    computeLockedPriceAtIndex(idx, getCartUnits(), getPageInfo(), getCdRules());
  }

  function dequeueLock(priceEl) {
    const idx = queueIndexOf(priceEl);
    if (idx === -1) return false;
    lockedQueue.splice(idx, 1);
    recomputeLockedQueueSequential(0);
    return true;
  }

  function updateQueueEntry(priceEl, newBase, newCompare) {
    const idx = queueIndexOf(priceEl);
    if (idx === -1) return;
    lockedQueue[idx].basePrice = newBase;
    lockedQueue[idx].comparePrice = newCompare;
    recomputeLockedQueueSequential(idx);
  }

  // Snapshot des items cochés pour les états de calcul. Pas d'isFixed : les
  // items cochés participent fluidement à l'allocation « Lowest priced first »,
  // ce qui permet à un nouvel item moins cher de leur prendre la remise.
  function getCheckedUnitsFromQueue() {
    pruneStaleQueueEntries();
    return lockedQueue.map((it) => ({
      productId: it.productId,
      basePrice: it.basePrice,
      comparePrice: it.comparePrice,
    }));
  }

  // ─── Core : allocation des remises ───────────────────────────────────────────
  //
  // Modèle d'allocation TB : « Lowest priced first » avec quota par palier.
  // Pour chaque règle TB :
  //   • on compte la quantité totale d'items éligibles (qty),
  //   • on détermine le palier le plus haut atteint (qty >= tier.qty),
  //   • on applique le pct du palier aux N items LES MOINS CHERS éligibles,
  //     où N = qty du palier atteint (capé au nombre d'items dispo).
  // → Ajouter un produit éligible peut donc faire perdre la remise à un produit
  //   plus cher qui l'avait, et la donner au nouvel arrivant.
  //
  // Les items isFixed (legacy) comptent dans le qty du palier mais sont
  // exclus de l'allocation (leur prix est figé).
  //
  // computeUnitDiscounts retourne le tableau u avec disc renseigné par item :
  // permet aux appelants de lire le prix post-remise individuel.
  // computeTotal somme simplement les contributions.
  function computeUnitDiscounts(units, cdRules) {
    const u = units.map((x) => ({
      productId: x.productId,
      basePrice: x.basePrice,
      comparePrice: x.comparePrice || 0,
      isFixed: !!x.isFixed,
      fixedPrice: x.fixedPrice,
      disc: 0,
    }));

    for (const rule of tbRules) {
      let qty = 0;
      for (const it of u) if (rule.productIdSet.has(it.productId)) qty++;
      let achievedTier = null;
      for (const t of rule.tiers) {
        if (qty >= t.qty) achievedTier = t;
        else break;
      }
      if (!achievedTier) continue;
      const pct = achievedTier.pct;
      if (pct <= 0) continue;
      const eligible = u.filter(
        (it) =>
          !it.isFixed && rule.productIdSet.has(it.productId) && pct > it.disc,
      );
      eligible.sort(
        (a, b) =>
          a.basePrice * (1 - a.disc / 100) - b.basePrice * (1 - b.disc / 100),
      );
      const slots = Math.min(achievedTier.qty, eligible.length);
      for (let i = 0; i < slots; i++) {
        eligible[i].disc = pct;
      }
    }

    for (const rule of cdRules) {
      let req = 0;
      for (const it of u) if (rule.requiredProductIds.has(it.productId)) req++;
      const quota = Math.floor(req / rule.requiredQty) * rule.discountedQty;
      if (quota <= 0) continue;
      const eligible = u.filter(
        (it) =>
          !it.isFixed &&
          rule.discountedProductIds.has(it.productId) &&
          rule.discountPct > it.disc,
      );
      eligible.sort(
        (a, b) =>
          a.basePrice * (1 - a.disc / 100) - b.basePrice * (1 - b.disc / 100),
      );
      const slots = Math.min(quota, eligible.length);
      for (let i = 0; i < slots; i++) {
        eligible[i].disc = rule.discountPct;
      }
    }

    return u;
  }

  function computeTotal(units, cdRules) {
    const u = computeUnitDiscounts(units, cdRules);
    let total = 0;
    for (const it of u) {
      if (it.isFixed) total += Math.max(0, it.fixedPrice);
      else total += it.basePrice * (1 - it.disc / 100);
    }
    return total;
  }

  // Coût MARGINAL d'un item dans le contexte d'un état :
  //   total(stateWithItem) − total(stateWithoutItem)
  //
  // Plafonné au prix de base (jamais d'affichage au-dessus du prix natif) et
  // planché à 0. Couvre le cas « Lowest priced first » : ajouter un produit
  // moins cher qui prend la remise à un plus cher peut coûter PLUS que le prix
  // de base du nouveau produit (parce qu'on perd la remise sur l'ancien). Dans
  // ce cas on s'arrête au prix de base, sans balise de réduction.
  function cappedMarginal(stateWithoutItem, stateWithItem, basePrice, cdRules) {
    const totalWith = computeTotal(stateWithItem, cdRules);
    const totalWithout = computeTotal(stateWithoutItem, cdRules);
    const marginal = Math.max(0, Math.round(totalWith - totalWithout));
    return Math.min(marginal, Math.max(0, basePrice));
  }

  // ─── Rendu DOM ───────────────────────────────────────────────────────────────

  function setTbActive(el, active) {
    if (active) el.setAttribute("data-tb-active", "");
    else el.removeAttribute("data-tb-active");
  }

  // Zones HORIZONTALES (cart drawer recents/complementary, comparison table,
  // etc.) : pas de container .badges, donc le span « Jusqu'à -X% » inline
  // est conservé en fromMode comme indicateur. Pas de badge inline (juste
  // prix + prix barré).
  function isInHorizontalZone(el) {
    return !!el.closest(".horizontal-product, .comparison-table");
  }

  function applyOutsidePrice(
    el,
    displayedPrice,
    basePrice,
    comparePrice,
    upToPctOverride,
  ) {
    const tbAffects = displayedPrice < basePrice;
    const fromMode = isFromMode(el);
    const horiz = isInHorizontalZone(el);

    if (fromMode) {
      // Mode « Dès … » : pas de strikethrough.
      // - Zones horizontales : on inclut le span .price__discount-range inline
      //   (pas de badge à part).
      // - Cards / fiche / modal : la badge externe gère le « Jusqu'à ». Pas
      //   de discount-range inline (CSS le masque en .product-card de toute
      //   façon).
      let upToPct = 0;
      if (horiz) {
        if (typeof upToPctOverride === "number" && upToPctOverride > 0) {
          upToPct = upToPctOverride;
        } else {
          const baseUpTo = parseFloat(el.dataset.tbBaseUpToPct) || 0;
          const tbPct =
            basePrice > 0
              ? ((basePrice - displayedPrice) * 100) / basePrice
              : 0;
          upToPct = Math.max(0, Math.round(Math.max(baseUpTo, tbPct)));
        }
      }
      el.classList.remove("price--on-sale");
      el.innerHTML = fromMarkup(displayedPrice, el, horiz, upToPct);
      setTbActive(el, tbAffects);
      return;
    }

    if (tbAffects) {
      const cap = comparePrice > basePrice ? comparePrice : basePrice;
      el.classList.add("price--on-sale");
      // Plus de badge inline en zones horizontales — juste prix + prix barré.
      el.innerHTML = discountedMarkup(Math.max(0, displayedPrice), cap, false);
      setTbActive(el, true);
    } else if (el.hasAttribute("data-tb-active")) {
      el.classList.toggle("price--on-sale", comparePrice > basePrice);
      el.innerHTML = regularMarkup(basePrice, comparePrice, false);
      setTbActive(el, false);
    }
  }

  function applyBundlePrice(el, displayedPrice, basePrice, comparePrice, opts) {
    const bundleItem = el.closest("[data-bundle-item]");
    const checkbox = bundleItem?.querySelector('input[name="bundles"]');
    const displayInt = Math.max(0, Math.round(displayedPrice));
    // forceVariantMode = true → ignore fromMode, render variant-specific
    // (badge « Epargnez X% » inline, prix avec strikethrough).
    const fromMode = opts?.forceVariantMode ? false : isFromMode(el);
    const upToPctOverride = opts?.upToPct;

    if (fromMode) {
      // Bundle item en mode « Dès » : on garde le span .price__discount-range
      // inline car il n'y a pas de container .badges à part dans un bundle item.
      let upToPct;
      if (typeof upToPctOverride === "number" && upToPctOverride > 0) {
        upToPct = upToPctOverride;
      } else {
        const baseUpTo = parseFloat(el.dataset.tbBaseUpToPct) || 0;
        const tbPct =
          basePrice > 0 ? ((basePrice - displayInt) * 100) / basePrice : 0;
        upToPct = Math.max(0, Math.round(Math.max(baseUpTo, tbPct)));
      }
      el.classList.remove("price--on-sale");
      el.innerHTML = fromMarkup(displayInt, el, true, upToPct);
    } else if (displayInt < basePrice) {
      // Bundle item avec remise : badge inline (pas de .badges container).
      const cap = comparePrice > basePrice ? comparePrice : basePrice;
      el.classList.add("price--on-sale");
      el.innerHTML = discountedMarkup(displayInt, cap, true);
    } else {
      // Pas de remise TB : on rend le markup normal en gardant le badge inline
      // si une promo Shopify (compare_at) est présente.
      el.classList.toggle("price--on-sale", comparePrice > basePrice);
      el.innerHTML = regularMarkup(basePrice, comparePrice, true);
    }
    if (checkbox) checkbox.setAttribute("data-price", displayInt);
    el.dataset.tbDisplayPrice = String(displayInt);
  }

  // Tiered bundle calcule le marginal ; la CD décide si l'item bundle doit
  // AFFICHER une promo (slotsAfterCart, cheapest-first…). Prend le meilleur prix.
  function mergeCdBundleDisplayPrice(el, basePrice, tbNet) {
    const display = Math.max(0, Math.round(tbNet));
    const cdApi = window.__cdBundlePricing;
    if (!cdApi?.computeItemState || !(basePrice > 0)) return display;
    const cdState = cdApi.computeItemState(el);
    if (!cdState?.active || !(cdState.discountPct > 0)) return display;
    const cdPrice = Math.round((basePrice * (100 - cdState.discountPct)) / 100);
    return Math.min(display, cdPrice);
  }

  // Le sticky reprend exactement le rendu du bouton ATC (innerHTML + data-price +
  // _originalHTML) pour garantir le même montant affiché et le même calcul.
  function getProductInfoForForm(form) {
    if (!form) return null;
    return (
      form.closest("product-info[data-product-id]") ||
      form
        .closest(".shopify-section, .featured-product, .quick-view, .x-modal")
        ?.querySelector("product-info[data-product-id]")
    );
  }

  function findAtcBuyPrice(form) {
    const productInfo = getProductInfoForForm(form);
    if (productInfo?.dataset.sectionId && productInfo?.dataset.productId) {
      const byId = document.querySelector(
        `#BuyButtonPrice-${productInfo.dataset.sectionId}-${productInfo.dataset.productId} product-buy-price[data-price]`,
      );
      if (byId) return byId;
    }
    if (!form?.id) return null;
    const formSel = CSS.escape(form.id);
    return (
      form.querySelector(`product-buy-price[form="${formSel}"][data-price]`) ||
      document.querySelector(
        `product-buy-price[form="${formSel}"][data-price]:not([data-sticky-atc-price])`,
      )
    );
  }

  function findStickyBuyPrice(form) {
    const productInfo = getProductInfoForForm(form);
    if (productInfo?.dataset.sectionId && productInfo?.dataset.productId) {
      const { sectionId, productId } = productInfo.dataset;
      const byId =
        document.querySelector(
          `#StickyBuyButtonPrice-${sectionId}-${productId} product-buy-price[data-price]`,
        ) ||
        document.querySelector(
          `#StickyPrice-${sectionId}-${productId} product-buy-price[data-price]`,
        );
      if (byId) return byId;
    }
    if (!form?.id) return null;
    const formSel = CSS.escape(form.id);
    return document.querySelector(
      `product-sticky-form[form="${formSel}"] product-buy-price[data-price]`,
    );
  }

  function getMainProductPriceEl(form) {
    const productInfo = getProductInfoForForm(form);
    if (!productInfo?.dataset.sectionId || !productInfo?.dataset.productId)
      return null;
    return document.querySelector(
      `#Price-${productInfo.dataset.sectionId}-${productInfo.dataset.productId} .price[data-base-price]`,
    );
  }

  function stickySaleMarkup(currentCents, compareCents) {
    return (
      `<span class="sr-only">${tbConfig.i18n.salePriceLabel}</span>` +
      `<span class="sticky-buy-price__current whitespace-nowrap">${formatMoney(currentCents)}</span>` +
      `<span class="sr-only">${tbConfig.i18n.regularPriceLabel}</span>` +
      `<span class="sticky-buy-price__compare inline-flex items-center h-auto relative whitespace-nowrap">${formatMoney(compareCents)}</span>`
    );
  }

  function renderStickyUnitPrice(
    stickyPrice,
    displayedInt,
    basePrice,
    comparePrice,
  ) {
    if (!Number.isFinite(displayedInt) || displayedInt < 0) return false;

    if (displayedInt < basePrice) {
      const cap = comparePrice > basePrice ? comparePrice : basePrice;
      stickyPrice.classList.add("price--on-sale");
      stickyPrice.innerHTML = stickySaleMarkup(displayedInt, cap);
      return true;
    }

    if (comparePrice > displayedInt && comparePrice > 0) {
      stickyPrice.classList.add("price--on-sale");
      stickyPrice.innerHTML = stickySaleMarkup(displayedInt, comparePrice);
      return true;
    }

    stickyPrice.classList.remove("price--on-sale");
    return false;
  }

  function syncStickyBuyPriceFromAtc(form) {
    const atcPrice = findAtcBuyPrice(form);
    const stickyPrice = findStickyBuyPrice(form);
    if (!atcPrice || !stickyPrice || atcPrice === stickyPrice) return;

    const atcDataPrice = atcPrice.getAttribute("data-price");
    if (atcDataPrice != null)
      stickyPrice.setAttribute("data-price", atcDataPrice);

    const section = form.closest(
      ".shopify-section, .featured-product, .quick-view, .x-modal",
    );
    const checkedCount = section
      ? section.querySelectorAll('input[name="bundles"]:checked').length
      : 0;

    if (checkedCount > 0) {
      stickyPrice.classList.remove("price--on-sale");
      stickyPrice.innerHTML = atcPrice.innerHTML;
    } else {
      const displayedInt = parseInt(atcDataPrice, 10);
      const mainPriceEl = getMainProductPriceEl(form);
      const basePrice = mainPriceEl
        ? parseInt(mainPriceEl.dataset.basePrice, 10) || displayedInt
        : displayedInt;
      const comparePrice = mainPriceEl
        ? parseInt(mainPriceEl.dataset.baseCompareAtPrice, 10) || 0
        : 0;

      if (
        !renderStickyUnitPrice(
          stickyPrice,
          displayedInt,
          basePrice,
          comparePrice,
        )
      ) {
        stickyPrice.innerHTML = atcPrice.innerHTML;
      }
    }

    if ("_originalHTML" in stickyPrice) {
      stickyPrice._originalHTML = stickyPrice.innerHTML;
    }

    const stickyRoot = stickyPrice.closest('[id^="StickyPrice-"]');
    const stickyBadge = stickyRoot?.querySelector("[data-sticky-bundle-badge]");
    if (!stickyBadge) return;
    if (checkedCount > 0) {
      stickyBadge.textContent = `+ ${checkedCount} article${checkedCount > 1 ? "s" : ""}`;
      stickyBadge.removeAttribute("hidden");
    } else {
      stickyBadge.setAttribute("hidden", "");
    }
  }

  function syncAllStickyBuyPricesFromAtc() {
    document
      .querySelectorAll('[data-type="add-to-cart-form"]')
      .forEach((form) => {
        syncStickyBuyPriceFromAtc(form);
      });
  }
  window.__syncStickyBuyPricesFromAtc = syncAllStickyBuyPricesFromAtc;
  window.__syncStickyBuyPriceFromAtc = syncStickyBuyPriceFromAtc;

  function applyProductBuyPrice(el, displayedPrice, basePrice, comparePrice) {
    const displayInt = Math.max(0, Math.round(displayedPrice));
    el.setAttribute("data-price", displayInt);

    let nested = el.querySelector(".price[data-base-price]");
    const hadNested = el.dataset.tbHasNested === "true";

    if (nested) {
      el.dataset.tbHasNested = "true";
      applyOutsidePrice(nested, displayInt, basePrice, comparePrice);
      if ("_originalHTML" in el) el._originalHTML = el.innerHTML;
      return;
    }

    if (hadNested) {
      // Sticky/StickyPrice : le nested .price a été wipé par la branche
      // « items > 0 » de product-buy-price.onBundleChanged (innerHTML = plain
      // money string). On régénère un .price[data-base-price] et on lui
      // applique le markup TB → le sticky retrouve son rendu stylé et le
      // _originalHTML est ré-aligné, ce qui élimine le flicker
      // « MAJ → minuscule → MAJ ».
      const productId = getProductIdForBuyPrice(el);
      const fresh = document.createElement("div");
      fresh.className = "price";
      fresh.setAttribute("data-base-price", basePrice);
      fresh.setAttribute("data-base-compare-at-price", comparePrice || 0);
      if (productId != null) fresh.setAttribute("data-product-id", productId);
      el.innerHTML = "";
      el.appendChild(fresh);
      applyOutsidePrice(fresh, displayInt, basePrice, comparePrice);
      if ("_originalHTML" in el) el._originalHTML = el.innerHTML;
      return;
    }

    // Cas BuyButtonPrice (ATC button) : pas de nested initialement. On rend
    // en TEXTE BRUT (sans spans .price__regular / .price__sale), car ces
    // classes ont un font-size propre (var(--fluid-sm-to-base)) qui rapetisse
    // le texte du bouton et change visuellement la casse héritée (text-transform
    // du .button). Le rendu natif Liquid de BuyButtonPrice est juste un
    // money string ; on s'aligne dessus pour conserver le style du bouton.
    el.innerHTML = formatMoney(displayInt);
    if ("_originalHTML" in el) el._originalHTML = el.innerHTML;
  }

  function getProductIdForBuyPrice(el) {
    const nested = el.querySelector(".price[data-product-id][data-base-price]");
    if (nested) return parseInt(nested.dataset.productId, 10);
    const formId = el.getAttribute("form");
    if (!formId) return null;
    const form = document.getElementById(formId);
    const scope = form?.closest(".shopify-section, .quick-view, .x-modal");
    const productInfo = scope?.querySelector("product-info[data-product-id]");
    return productInfo ? parseInt(productInfo.dataset.productId, 10) : null;
  }

  // ─── Sélecteur de variantes d'un item du bloc additionnel ───────────────────

  function updateBundleVariantSelectForItem(
    bundleItem,
    stateForOption,
    cdRules,
  ) {
    const select = bundleItem.querySelector("[data-bundle-variant-select]");
    if (!select) return;
    const pid = parseInt(bundleItem.dataset.productId, 10);
    if (!Number.isFinite(pid)) return;

    Array.from(select.options).forEach((opt) => {
      if (opt.disabled) return;
      const variantPrice = parseInt(opt.dataset.price, 10);
      const variantCompare = parseInt(opt.dataset.comparePrice, 10) || 0;
      if (!Number.isFinite(variantPrice) || variantPrice <= 0) return;
      // Prix affiché de chaque option = coût MARGINAL capé au prix de la
      // variante. Cohérent avec le prix du bundle item lui-même.
      const withOpt = [
        ...stateForOption,
        {
          productId: pid,
          basePrice: variantPrice,
          comparePrice: variantCompare,
        },
      ];
      const net = cappedMarginal(
        stateForOption,
        withOpt,
        variantPrice,
        cdRules,
      );

      if (!opt.dataset.tbOriginalText) opt.dataset.tbOriginalText = opt.text;
      const originalText = opt.dataset.tbOriginalText;
      const originalMoney = formatMoney(variantPrice);
      const newMoney = formatMoney(net);
      if (net !== variantPrice && originalText.indexOf(originalMoney) !== -1) {
        opt.text = originalText.replace(originalMoney, newMoney);
      } else {
        opt.text = originalText;
      }
    });
  }

  // ─── Boucle d'évaluation ─────────────────────────────────────────────────────

  let evaluating = false;
  function evaluateAndApply() {
    if (evaluating) return;
    evaluating = true;
    try {
      _evaluate();
    } finally {
      evaluating = false;
    }
  }

  // État panier + qty fiche + items cochés SAUF celui d'index idx.
  function buildStateExcludingQueueIndex(idx, cartUnits, pageInfo) {
    return buildStateUpToQueueIndex(idx, cartUnits, pageInfo);
  }

  function _evaluate() {
    syncLockedQueueWithDom();
    const cdRules = getCdRules();
    const cartUnits = getCartUnits();
    const contexts = getPageContexts();
    const ficheCtx =
      contexts.find((c) => c.scope === document) || contexts[0] || null;

    const checkedUnits = getCheckedUnitsFromQueue();

    // baseStateUnits : panier + qty de la FICHE (pour la file de locks et les
    // items du bloc additionnel rattachés à la fiche).
    const baseStateUnits = [...cartUnits];
    if (ficheCtx) {
      for (let i = 0; i < ficheCtx.qty; i++) {
        baseStateUnits.push({
          productId: ficheCtx.productId,
          basePrice: ficheCtx.basePrice,
          comparePrice: ficheCtx.comparePrice,
        });
      }
    }
    for (const u of checkedUnits) baseStateUnits.push(u);

    // Ensemble des priceEl déjà traités par les contextes (à exclure de la
    // boucle « cartes »).
    const handledPriceEls = new Set();

    // Helper : calcule + applique le rendu pour TOUS les .price d'un contexte
    // (titre + tout .price interne d'un product-buy-price du même produit dans
    // le même scope). Évite la désync entre prix du titre et prix du sticky.
    function processContextPriceEl(ctx) {
      if (ctx.productInfo && !shouldShowVariantPreview(ctx.productInfo)) {
        const selectedVariant = detectVariantInScope(ctx.productInfo);
        if (selectedVariant) {
          const variantPrice = parseInt(selectedVariant.price, 10);
          const variantCompare =
            parseInt(selectedVariant.compare_at_price, 10) || 0;
          if (Number.isFinite(variantPrice) && variantPrice > 0) {
            ctx.basePrice = variantPrice;
            ctx.comparePrice = variantCompare;
            ctx.priceEl.dataset.basePrice = String(variantPrice);
            ctx.priceEl.dataset.baseCompareAtPrice = String(variantCompare);
          }
        }
      }

      const fromMode = isFromMode(ctx.priceEl);
      const variants = getProductVariants(ctx.productId, ctx.priceEl);
      const previewPending =
        ctx.productInfo && shouldShowVariantPreview(ctx.productInfo);
      const samePricePreview =
        previewPending && hasSamePriceVariantSale(variants)
          ? getSamePriceSalePreviewData(variants)
          : null;

      // Prix affiché du produit principal = coût MARGINAL par unité capé au
      // prix de base. État de référence = panier UNIQUEMENT (pas les items
      // cochés du bloc additionnel) : la fiche reste stable quand l'utilisateur
      // coche/décoche, et une réduction conditionnelle déclenchée par un item
      // coché ne vient pas réduire le prix de la fiche.
      const pageBase = samePricePreview
        ? samePricePreview.minPrice
        : ctx.basePrice;
      const pageCompare = samePricePreview
        ? samePricePreview.maxCompareAt
        : ctx.comparePrice;
      const withCtx = [
        ...cartUnits,
        ...Array.from({ length: ctx.qty }, () => ({
          productId: ctx.productId,
          basePrice: pageBase,
          comparePrice: pageCompare,
        })),
      ];
      const aggregateMarginal = Math.max(
        0,
        Math.round(
          computeTotal(withCtx, cdRules) - computeTotal(cartUnits, cdRules),
        ),
      );
      const perUnitMarginal =
        ctx.qty > 0 ? Math.round(aggregateMarginal / ctx.qty) : 0;
      const displayedUnit = Math.min(perUnitMarginal, Math.max(0, pageBase));

      let displayBase = pageBase;
      let compareForDisplay = pageCompare;
      let displayed = displayedUnit;

      // Mode « Dès » : marginal capé d'UNE copie à priceMin ajoutée au panier
      // seul. Aperçu de la variante la moins chère, stable lui aussi.
      if (fromMode && ctx.priceEl.dataset.tbPriceMin) {
        const priceMin = parseInt(ctx.priceEl.dataset.tbPriceMin, 10);
        if (Number.isFinite(priceMin) && priceMin > 0) {
          displayBase = priceMin;
          const stateMin = [
            ...cartUnits,
            { productId: ctx.productId, basePrice: priceMin, comparePrice: 0 },
          ];
          displayed = cappedMarginal(cartUnits, stateMin, priceMin, cdRules);
        }
      }

      // Calcule upToPct AVANT de rendre les .price : on cherche le MAX réel
      // parmi toutes les variantes (en réappliquant TB+CD à chacune), pour
      // que « Jusqu'à -X% » reflète l'économie maximale observable et pas
      // seulement le tbPct de priceMin. État = panier seul pour rester stable
      // face aux cochages du bloc additionnel (cohérent avec displayed).
      const baseUpTo = parseFloat(ctx.priceEl.dataset.tbBaseUpToPct) || 0;
      const tbPct =
        displayBase > 0 ? ((displayBase - displayed) * 100) / displayBase : 0;
      let upToPct = Math.max(0, Math.round(Math.max(baseUpTo, tbPct)));
      if (fromMode) {
        const variantsMax = computeMaxDiscountAcrossVariants(
          ctx.productId,
          cartUnits,
          cdRules,
          ctx.priceEl,
        );
        if (variantsMax > upToPct) upToPct = variantsMax;
      }

      // Applique le rendu à TOUS les .price du produit dans le scope du
      // contexte (titre principal). Le sticky add to cart reprend le prix du
      // bouton ATC via syncStickyBuyPriceFromAtc (évite le prix « Dès » / nested).
      const root = ctx.scope === document ? document : ctx.scope;
      const ownPriceEls = root.querySelectorAll(
        `.price[data-product-id="${ctx.productId}"][data-base-price]:not([data-bundle-price])`,
      );
      ownPriceEls.forEach((pEl) => {
        if (pEl.closest("product-sticky-form")) return;
        applyOutsidePrice(
          pEl,
          displayed,
          displayBase,
          compareForDisplay,
          upToPct,
        );
        handledPriceEls.add(pEl);
      });

      applyTbBadge(
        ctx.productId,
        displayed,
        displayBase,
        compareForDisplay,
        fromMode,
        upToPct,
        ctx.scope,
      );

      const unitDisplayed = Math.max(0, Math.round(displayedUnit));
      root.querySelectorAll("product-buy-price[data-price]").forEach((bp) => {
        const bpId = getProductIdForBuyPrice(bp);
        if (bpId !== ctx.productId) return;
        // Sticky : copié depuis le bouton ATC après calcul (syncStickyBuyPriceFromAtc).
        if (
          bp.closest("product-sticky-form") ||
          bp.hasAttribute("data-sticky-atc-price")
        )
          return;

        const nested = bp.querySelector(".price[data-base-price]");
        const bpBase = nested
          ? parseInt(nested.dataset.basePrice, 10)
          : parseInt(bp.getAttribute("data-price"), 10);
        if (!Number.isFinite(bpBase) || bpBase <= 0) return;

        applyProductBuyPrice(bp, unitDisplayed, displayBase, compareForDisplay);
      });

      if (ctx.productInfo) {
        const form = ctx.productInfo.querySelector(
          'form[data-type="add-to-cart-form"]',
        );
        if (form) syncStickyBuyPriceFromAtc(form);
      }
    }

    // ── Contextes « page » (fiche + chaque modal ouvert) ────────────────────
    for (const ctx of contexts) processContextPriceEl(ctx);

    // ── Cartes / cart drawer / recos hors contextes ─────────────────────────
    document
      .querySelectorAll(".price[data-base-price][data-product-id]")
      .forEach((el) => {
        if (el.hasAttribute("data-bundle-price")) return;
        if (handledPriceEls.has(el)) return;
        const pid = parseInt(el.dataset.productId, 10);
        if (!Number.isFinite(pid)) return;
        const basePrice = parseInt(el.dataset.basePrice, 10);
        const comparePrice = parseInt(el.dataset.baseCompareAtPrice, 10) || 0;
        if (!Number.isFinite(basePrice) || basePrice <= 0) return;

        // Cartes hors contexte (grilles, recos, drawer) : prix affiché = coût
        // MARGINAL capé au prix de base. Cohérent avec « Lowest priced first »
        // — si ajouter ce produit déplace une remise sur un produit du panier
        // et que le coût dépasse le prix de base, on cap (pas de balise).
        const fromMode = isFromMode(el);
        let displayBase = basePrice;
        let displayed;
        const computeCardNet = (bp, cp) => {
          const withP = [
            ...cartUnits,
            { productId: pid, basePrice: bp, comparePrice: cp },
          ];
          return cappedMarginal(cartUnits, withP, bp, cdRules);
        };
        if (fromMode && el.dataset.tbPriceMin) {
          const priceMin = parseInt(el.dataset.tbPriceMin, 10);
          if (Number.isFinite(priceMin) && priceMin > 0) {
            displayBase = priceMin;
            displayed = computeCardNet(priceMin, 0);
          } else {
            displayed = computeCardNet(basePrice, comparePrice);
          }
        } else {
          displayed = computeCardNet(basePrice, comparePrice);
        }

        const baseUpTo = parseFloat(el.dataset.tbBaseUpToPct) || 0;
        const tbPct =
          displayBase > 0 ? ((displayBase - displayed) * 100) / displayBase : 0;
        let upToPct = Math.max(0, Math.round(Math.max(baseUpTo, tbPct)));
        if (fromMode) {
          const variantsMax = computeMaxDiscountAcrossVariants(
            pid,
            cartUnits,
            cdRules,
            el,
          );
          if (variantsMax > upToPct) upToPct = variantsMax;
        }

        const cardScope =
          el.closest(".shopify-section, .quick-view, .x-modal, section") ||
          document;
        applyOutsidePrice(el, displayed, displayBase, comparePrice, upToPct);
        applyTbBadge(
          pid,
          displayed,
          displayBase,
          comparePrice,
          fromMode,
          upToPct,
          cardScope,
        );
      });

    // ── product-buy-price hors contexte (rare) ──────────────────────────────
    const handledBuyPrices = new Set();
    contexts.forEach((ctx) => {
      const root = ctx.scope === document ? document : ctx.scope;
      root.querySelectorAll("product-buy-price[data-price]").forEach((bp) => {
        const bpId = getProductIdForBuyPrice(bp);
        if (bpId === ctx.productId) handledBuyPrices.add(bp);
      });
    });
    document.querySelectorAll("product-buy-price[data-price]").forEach((el) => {
      if (handledBuyPrices.has(el)) return;
      const pid = getProductIdForBuyPrice(el);
      if (!Number.isFinite(pid)) return;
      const nested = el.querySelector(".price[data-base-price]");
      const basePrice = nested
        ? parseInt(nested.dataset.basePrice, 10)
        : parseInt(el.getAttribute("data-price"), 10);
      const comparePrice = nested
        ? parseInt(nested.dataset.baseCompareAtPrice, 10) || 0
        : 0;
      if (!Number.isFinite(basePrice) || basePrice <= 0) return;
      const withP = [...cartUnits, { productId: pid, basePrice, comparePrice }];
      const displayed = cappedMarginal(cartUnits, withP, basePrice, cdRules);
      applyProductBuyPrice(el, displayed, basePrice, comparePrice);
    });

    // ── Items du bloc additionnel ──────────────────────────────────────────
    document.querySelectorAll("[data-bundle-price]").forEach((el) => {
      const pid = parseInt(el.dataset.productId, 10);
      if (!Number.isFinite(pid)) return;

      const bundleItem = el.closest("[data-bundle-item]");
      const checkbox = bundleItem?.querySelector('input[name="bundles"]');
      const isChecked = checkbox?.checked === true;

      // Lecture prioritaire depuis le sélecteur de variantes (option courante),
      // qui reflète la SÉLECTION RÉELLE de l'utilisateur — évite la désync
      // affichée si data-current-price n'a pas encore été synchronisé par
      // custom_product-card.js.
      let basePrice = 0;
      let comparePrice = 0;
      const select = bundleItem?.querySelector("[data-bundle-variant-select]");
      const opt = select?.selectedOptions?.[0];
      if (opt) {
        const p = parseInt(opt.dataset.price, 10);
        const c = parseInt(opt.dataset.comparePrice, 10) || 0;
        if (Number.isFinite(p) && p > 0) {
          basePrice = p;
          if (c > p) comparePrice = c;
        }
      }
      if (basePrice <= 0) {
        basePrice =
          parseInt(el.dataset.currentPrice, 10) ||
          parseInt(el.dataset.priceMin, 10) ||
          0;
        comparePrice =
          parseInt(el.dataset.currentComparePrice, 10) ||
          parseInt(el.dataset.comparePrice, 10) ||
          0;
      }
      if (basePrice <= 0) return;

      if (isChecked) {
        // Coché → on affiche le PRIX SPÉCIFIQUE de la variante (pas « Dès … »),
        // avec badge inline « Epargnez X% » pour chaque variante.
        const qIdx = queueIndexOf(el);
        if (qIdx === -1) enqueueLock(el, pid, basePrice, comparePrice);
        const entry = lockedQueue[queueIndexOf(el)];
        if (entry) {
          applyBundlePrice(
            el,
            mergeCdBundleDisplayPrice(el, basePrice, entry.lockedPrice),
            basePrice,
            comparePrice,
            {
              forceVariantMode: true,
            },
          );
          if (bundleItem) {
            const stateForOpt = buildStateExcludingQueueIndex(
              queueIndexOf(el),
              cartUnits,
              ficheCtx,
            );
            updateBundleVariantSelectForItem(bundleItem, stateForOpt, cdRules);
          }
        }
        return;
      }

      // Non coché → on revient à l'affichage GLOBAL.
      //   • Multi-variantes à prix différents (priceMax > priceMin) :
      //     « Dès <marginal pour priceMin> » + « Jusqu'à -X% off »
      //     (X = max parmi toutes les variantes, lu via data-tb-variants).
      //   • Variantes au même prix (priceMax == priceMin), avec prix barrés
      //     différents : prix normal + strikethrough sur le MAX compare_at
      //     parmi les variantes + badge inline « Epargnez X% ».
      const priceMin = parseInt(el.dataset.priceMin, 10) || basePrice;
      const priceMax = parseInt(el.dataset.priceMax, 10) || priceMin;
      const isVariantRange = priceMax > priceMin;

      // Calcule maxCompareAt = max(compare_at) parmi les variantes où
      // compare_at > price (sinon 0).
      const variants = getProductVariants(pid, el);
      let maxCompareAt = 0;
      if (variants) {
        for (const v of variants) {
          const vp = parseInt(v.price, 10) || 0;
          const vc = parseInt(v.compare_at_price, 10) || 0;
          if (vc > vp && vc > maxCompareAt) maxCompareAt = vc;
        }
      }

      // Pour le « Jusqu'à -X% » des bundle items non cochés, on calcule le max
      // contre l'ÉTAT COMPLET (panier + page + items cochés) — pas seulement
      // le panier — pour que la valeur reflète bien la remise que la variante
      // déclencherait DANS LE CONTEXTE ACTUEL de la fiche.
      const variantsMax = computeMaxDiscountAcrossVariants(
        pid,
        baseStateUnits,
        cdRules,
        el,
      );

      // Prix preview = coût MARGINAL capé au prix de base. Inclut la perte de
      // remise sur un item plus cher déplacé : si l'ajout coûte plus que le
      // prix de base, on cap au prix de base et l'item s'affiche sans balise
      // de réduction (cohérent avec les items cochés).
      if (isVariantRange) {
        const withQ = [
          ...baseStateUnits,
          { productId: pid, basePrice: priceMin, comparePrice: 0 },
        ];
        const net = cappedMarginal(baseStateUnits, withQ, priceMin, cdRules);
        applyBundlePrice(
          el,
          mergeCdBundleDisplayPrice(el, priceMin, net),
          priceMin,
          0,
          { upToPct: variantsMax },
        );
      } else {
        const refCompare = maxCompareAt > basePrice ? maxCompareAt : 0;
        const withQ = [
          ...baseStateUnits,
          { productId: pid, basePrice, comparePrice: refCompare },
        ];
        const net = cappedMarginal(baseStateUnits, withQ, basePrice, cdRules);
        applyBundlePrice(
          el,
          mergeCdBundleDisplayPrice(el, basePrice, net),
          basePrice,
          refCompare,
        );
      }

      if (bundleItem) {
        updateBundleVariantSelectForItem(bundleItem, baseStateUnits, cdRules);
      }
    });

    refireBundleChange();
  }

  // Flag pour distinguer un bundle:change émis par TB d'un émis par d'autres
  // scripts (theme.js ProductBundleDetails, custom_preselect-variant…). Permet
  // au listener bundle:change de re-évaluer sur dispatch externe sans boucler
  // sur les nôtres.
  let dispatchingBundleChange = false;
  function refireBundleChange() {
    dispatchingBundleChange = true;
    try {
      document
        .querySelectorAll('[data-type="add-to-cart-form"]')
        .forEach((form) => {
          const scope = form.closest(".shopify-section, .quick-view, .x-modal");
          const checkedBundles = scope
            ? Array.from(
                scope.querySelectorAll('input[name="bundles"]:checked'),
              )
            : [];
          form.dispatchEvent(
            new CustomEvent("bundle:change", {
              detail: {
                items: checkedBundles.map((input) => ({
                  id: input.value,
                  quantity: 1,
                  price: input.getAttribute("data-price"),
                })),
              },
            }),
          );
        });
    } finally {
      dispatchingBundleChange = false;
    }
    syncAllStickyBuyPricesFromAtc();
  }

  // ─── Détection de variante depuis le DOM (pour le pré-rendu sans flash) ────

  function detectVariantInScope(productInfo) {
    if (!productInfo || !hasCompleteVariantSelection(productInfo)) return null;
    const variantPicker = productInfo.querySelector("variant-picker");
    if (!variantPicker) return null;

    const totalOptions = getVariantPickerOptionCount(variantPicker);
    const checkedInputs = Array.from(
      variantPicker.querySelectorAll('input[type="radio"]:checked'),
    );
    if (totalOptions > 0 && checkedInputs.length < totalOptions) return null;

    const selectedOptions = checkedInputs.map((inp) => inp.value);
    const selects = Array.from(variantPicker.querySelectorAll("select"));
    selects.forEach((sel) => {
      if (sel.value !== "" && sel.value != null)
        selectedOptions.push(sel.value);
    });
    if (selectedOptions.length === 0) return null;

    const scriptTag = productInfo.querySelector(
      "script[data-product-variant-data]",
    );
    if (!scriptTag) return null;
    let data;
    try {
      data = JSON.parse(scriptTag.textContent);
    } catch (e) {
      return null;
    }
    const variants = data.productVariants;
    if (!Array.isArray(variants)) return null;
    return (
      variants.find(
        (v) =>
          v.options &&
          v.options.length === selectedOptions.length &&
          selectedOptions.every((opt, i) => v.options[i] === opt),
      ) || null
    );
  }

  // Pré-mise à jour des data-* prix quand une variante COMPLÈTE est sélectionnée
  // (optionValueSelectionChange, avant fetch theme.js). Le badge est recalculé
  // par evaluateAndApply (scope modal/fiche + TB marginal).
  function handleOptionValueSelection(payload) {
    const eventTarget =
      payload?.data?.event?.target || payload?.data?.target || null;
    const clickedInfo = eventTarget?.closest?.("product-info[data-product-id]");
    if (!clickedInfo) {
      evaluateAndApply();
      return;
    }

    if (shouldShowVariantPreview(clickedInfo)) {
      evaluateAndApply();
      return;
    }

    const variant = detectVariantInScope(clickedInfo);
    if (!variant) {
      evaluateAndApply();
      return;
    }

    const pid = parseInt(clickedInfo.dataset.productId, 10);
    if (!Number.isFinite(pid)) {
      evaluateAndApply();
      return;
    }

    const scope = getScopeForProductInfo(clickedInfo);
    const root = scope === document ? document : scope;
    root
      .querySelectorAll(
        `.price[data-product-id="${pid}"][data-base-price]:not([data-bundle-price])`,
      )
      .forEach((priceEl) => {
        if (variant.price != null)
          priceEl.dataset.basePrice = String(variant.price);
        priceEl.dataset.baseCompareAtPrice = String(
          variant.compare_at_price || 0,
        );
        priceEl.dataset.tbVariantSelected = "true";
        priceEl
          .querySelectorAll(".price__regular--from")
          .forEach((s) => s.remove());
      });

    evaluateAndApply();
  }

  // ─── Coche / décoche / variante ─────────────────────────────────────────────

  function handleCheckboxChange(cb) {
    const bundleItem = cb.closest("[data-bundle-item]");
    const priceEl = bundleItem?.querySelector("[data-bundle-price]");
    if (!priceEl || !bundleItem) return;
    const pid = parseInt(bundleItem.dataset.productId, 10);
    if (!Number.isFinite(pid)) return;

    // Lecture prioritaire : sélecteur de variante (option courante), pour
    // éviter le décalage si data-current-price n'a pas encore été mis à jour
    // par custom_product-card.js's applySelection.
    let basePrice = 0;
    let comparePrice = 0;
    const select = bundleItem.querySelector("[data-bundle-variant-select]");
    const opt = select?.selectedOptions?.[0];
    if (opt) {
      const p = parseInt(opt.dataset.price, 10);
      const c = parseInt(opt.dataset.comparePrice, 10) || 0;
      if (Number.isFinite(p) && p > 0) {
        basePrice = p;
        if (c > p) comparePrice = c;
      }
    }
    if (basePrice <= 0) {
      basePrice =
        parseInt(priceEl.dataset.currentPrice, 10) ||
        parseInt(priceEl.dataset.priceMin, 10) ||
        0;
      comparePrice =
        parseInt(priceEl.dataset.currentComparePrice, 10) ||
        parseInt(priceEl.dataset.comparePrice, 10) ||
        0;
    }

    if (cb.checked) enqueueLock(priceEl, pid, basePrice, comparePrice);
    else dequeueLock(priceEl);
  }

  function handleBundleVariantChange(select) {
    const bundleItem = select.closest("[data-bundle-item]");
    const priceEl = bundleItem?.querySelector("[data-bundle-price]");
    if (!priceEl) return;
    // On lit le prix de la variante DIRECTEMENT depuis l'option sélectionnée
    // du <select> (data-price), pas via data-current-price qui peut être
    // encore stale au moment où ce capture handler tourne (custom_product-card.js
    // applySelection ne s'est pas encore exécutée).
    const opt = select.selectedOptions?.[0];
    const newBase = parseInt(opt?.dataset?.price, 10);
    let newCompare = parseInt(opt?.dataset?.comparePrice, 10) || 0;
    if (newCompare && newCompare <= newBase) newCompare = 0;
    if (!Number.isFinite(newBase) || newBase <= 0) return;
    const idx = queueIndexOf(priceEl);
    if (idx !== -1) updateQueueEntry(priceEl, newBase, newCompare);
  }

  // ─── Init / événements ───────────────────────────────────────────────────────

  async function fetchCart() {
    try {
      const res = await fetch("/cart.js");
      const data = await res.json();
      if (data && Array.isArray(data.items)) {
        cartData = {
          items: data.items.map((it) => ({
            product_id: it.product_id,
            quantity: it.quantity,
            price: it.original_price ?? it.price ?? 0,
            compare_at_price:
              it.compare_at_price ?? it.variant?.compare_at_price ?? 0,
          })),
        };
      }
    } catch (_) {}
  }

  // Debounce simple pour éviter les rafales d'eval (modal init, etc.)
  let scheduled = false;
  function scheduleEval() {
    if (scheduled) return;
    scheduled = true;
    Promise.resolve().then(() => {
      scheduled = false;
      evaluateAndApply();
    });
  }

  function applyCartUpdate(cart) {
    if (cart && Array.isArray(cart.items)) {
      cartData = {
        items: cart.items.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          price: it.original_price ?? it.price ?? 0,
          compare_at_price:
            it.compare_at_price ?? it.variant?.compare_at_price ?? 0,
        })),
      };
    }
    // ProductBundleDetails décoche les bundles sur cartUpdate (source product-form)
    // de façon synchrone — on recalcule après pour ne pas compter des coches
    // périmées ni recalculer les prix figés avant le décochage.
    Promise.resolve().then(() => {
      syncLockedQueueWithDom();
      recomputeLockedQueueSequential(0);
      evaluateAndApply();
    });
  }

  function bindEvents() {
    document.addEventListener("cart:updated", (e) => {
      applyCartUpdate(e.detail?.cart);
    });

    if (window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (payload) => {
          applyCartUpdate(payload?.cart);
        },
      );
    }

    // Option click (AVANT fetch) : pré-mise à jour du .price element avec les
    // données de la variante détectée côté DOM. Élimine le flash entre la
    // variante précédente et la nouvelle.
    if (window.theme?.pubsub?.PUB_SUB_EVENTS?.optionValueSelectionChange) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
        handleOptionValueSelection,
      );
    }

    // Fallback DOM : si le pubsub n'est pas tiré (theme custom, override, etc.)
    // on capte aussi un change DOM natif dans un variant-picker pour mettre à
    // jour le prix + le badge per-variant.
    document.addEventListener("change", (e) => {
      const picker = e.target?.closest?.("variant-picker");
      if (!picker) return;
      handleOptionValueSelection({
        data: { event: e, target: e.target },
      });
    });

    document.addEventListener(
      "click",
      (e) => {
        const target = e.target;
        if (!target || !target.closest) return;
        const picker = target.closest("variant-picker");
        if (!picker) return;
        scheduleEval();
      },
      true,
    );

    // Quand custom_preselect-variant marque la sélection comme complète
    document.addEventListener(
      "variant-validation:complete",
      () => scheduleEval(),
      true,
    );

    // Variant change (APRÈS fetch + updateSourceFromDestination). Capture :
    // applique TB avant product-buy-price.onBundleChanged (bubble).
    const variantEvalAfterTheme = (event) => {
      const variant = event?.detail?.variant ?? event?.data?.variant;
      const productId = event?.data?.productId;
      if (variant?.price != null) {
        const infos = productId
          ? document.querySelectorAll(
              `product-info[data-product-id="${productId}"]`,
            )
          : event.target?.closest
            ? [event.target.closest("product-info[data-product-id]")].filter(
                Boolean,
              )
            : [];
        const variantPrice = parseInt(variant.price, 10) || 0;
        const variantCompare = parseInt(variant.compare_at_price, 10) || 0;
        infos.forEach((pi) => {
          const pid = parseInt(pi.dataset.productId, 10);
          if (!Number.isFinite(pid)) return;
          const scope = getScopeForProductInfo(pi);
          const root = scope === document ? document : scope;
          root
            .querySelectorAll(
              `.price[data-product-id="${pid}"][data-base-price]:not([data-bundle-price])`,
            )
            .forEach((priceEl) => {
              priceEl.dataset.basePrice = String(variantPrice);
              priceEl.dataset.baseCompareAtPrice = String(variantCompare);
              priceEl.dataset.tbVariantSelected = "true";
            });
        });
      }
      evaluateAndApply();
      scheduleEval();
      setTimeout(scheduleEval, 50);
      setTimeout(scheduleEval, 200);
      setTimeout(scheduleEval, 450);
      requestAnimationFrame(syncAllStickyBuyPricesFromAtc);
      setTimeout(syncAllStickyBuyPricesFromAtc, 50);
      setTimeout(syncAllStickyBuyPricesFromAtc, 200);
    };

    if (window.theme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        variantEvalAfterTheme,
      );
    }
    document.addEventListener("variant:change", variantEvalAfterTheme, true);

    document.addEventListener("bundle:change", () => {
      if (dispatchingBundleChange) return;
      scheduleEval();
      requestAnimationFrame(syncAllStickyBuyPricesFromAtc);
    });

    document.addEventListener("input", (e) => {
      if (
        e.target.matches('input[type="number"]') &&
        e.target.closest("quantity-input")
      ) {
        evaluateAndApply();
      }
    });

    // Capture (avant l'eval microtask) pour gérer la file de locks
    document.addEventListener(
      "change",
      (e) => {
        if (e.target.matches('input[name="bundles"]')) {
          handleCheckboxChange(e.target);
        } else if (e.target.matches("[data-bundle-variant-select]")) {
          handleBundleVariantChange(e.target);
        }
      },
      true,
    );

    document.addEventListener("change", (e) => {
      if (
        e.target.matches('input[name="bundles"]') ||
        e.target.matches("[data-bundle-variant-select]") ||
        (e.target.closest("[data-bundle-item]") &&
          !e.target.matches('input[name="bundles"]'))
      ) {
        scheduleEval();
      }
    });

    // Grille collection rechargée (filtres AJAX, infinite scroll)
    document.addEventListener("collection:reloaded", scheduleEval);

    // Contenus dynamiques / modals
    document.addEventListener("recentlyViewed:loaded", scheduleEval, true);
    document.addEventListener("recommendations:loaded", scheduleEval, true);
    document.addEventListener("quickview:loaded", scheduleEval);
    document.addEventListener("quickview:open", scheduleEval);
    document.addEventListener("modal:afterShow", scheduleEval);
    document.addEventListener("bundles:initialized", scheduleEval);
  }

  function init() {
    document
      .querySelectorAll('[data-bundle-item] input[name="bundles"]:checked')
      .forEach((cb) => handleCheckboxChange(cb));
    evaluateAndApply();
    bindEvents();
    fetchCart().then(() => {
      recomputeLockedQueueSequential(0);
      evaluateAndApply();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
